/// <amd-module name="Wit/WorkItemFormMarkdown/WorkItemFormMarkdownToolbar" />
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { format } from "VSS/Core/Util/String";
import { IMarkdownToolbarCommand, toolbarCommands } from "VSS/Features/MarkdownEditor/Commands/Toolbar";
import { IMarkdownToolbarProps, MarkdownToolbar } from "VSS/Features/MarkdownEditor/Components";
import { insertText, wrapWithSign } from "VSS/Features/MarkdownEditor/Formatting/Common";
import { IMarkdownFormattingArgs, IMarkdownFormattingResult } from "VSS/Features/MarkdownEditor/Formatting/Contracts";
import { MessageDialog } from "VSS/Features/PlatformUI/MessageDialog";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { FileInput, FileInputContentType, FileInputData } from "VSSUI/FileInput";
import { IIconProps } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import { IndeterminateProgressBar } from "VSSUI/ProgressBar";
import * as Resources from "Wit/WorkItemFormMarkdown/Resources";
import { validFileTypeList } from "./FileUploadUtils";

export type WorkItemFormMarkdownToolbarProps = IMarkdownToolbarProps & {
    pageContext: IVssPageContext;
    uploadImage: (file: File) => Promise<string>;
    hide?: boolean;
    debounceHideMs?: number;
};

export const WorkItemFormMarkdownToolbar: React.FC<WorkItemFormMarkdownToolbarProps> = ({ pageContext, uploadImage, ...props }) => {
    const content = (
        <MarkdownToolbar
            commands={[
                ...toolbarCommands,
                new PullRequestMentionCommand(),
                new WorkItemMentionCommand(),
                new SomeoneMentionCommand(),
                new HighlightCommand(),
                new AttachmentCommand({ pageContext, uploadImage })
            ]}
            className="flex-grow work-item-markdown-toolbar"
            {...props}
        />
    );

    const [hidden, setHidden] = React.useState(props.hide);
    const timerManagement = React.useMemo(() => new TimerManagement(), []);
    const debouncedSetHidden: (hidden: boolean | undefined) => void = React.useMemo(
        () =>
            timerManagement.debounce(
                (hidden: boolean | undefined) => {
                    setHidden(hidden);
                },
                props.debounceHideMs,
                { trailing: true }
            ),
        [setHidden, timerManagement]
    );
    React.useEffect(() => {
        if (props.hide) {
            debouncedSetHidden(props.hide);
        } else {
            debouncedSetHidden(false);
            setHidden(false);
        }
    }, [props.hide]);
    return hidden ? null : content;
};

class SomeoneMentionCommand implements IMarkdownToolbarCommand {
    private someoneCharacter = "@";
    public id = "someone-mention";
    public iconProps: IIconProps = { iconName: "Accounts" };
    public text = Resources.MarkdownEditorMentionSomeone;
    public rank = 110;

    public formatText = (formattingArgs: IMarkdownFormattingArgs): IMarkdownFormattingResult => insertText(formattingArgs, this.someoneCharacter);
}

class WorkItemMentionCommand implements IMarkdownToolbarCommand {
    private workItemCharacter = "#";
    public id = "wi-mention";
    public iconProps: IIconProps = { iconName: "NumberSymbol" };
    public text = Resources.MarkdownEditorMentionWorkItem;
    public rank = 111;

    public formatText = (formattingArgs: IMarkdownFormattingArgs): IMarkdownFormattingResult => insertText(formattingArgs, this.workItemCharacter);
}

class PullRequestMentionCommand implements IMarkdownToolbarCommand {
    private pullRequestCharacter = "!";
    public id = "pr-mention";
    public iconProps: IIconProps = { iconName: "BranchPullRequest" };
    public text = Resources.MarkdownEditorMentionPullRequest;
    public rank = 112;

    public formatText = (formattingArgs: IMarkdownFormattingArgs): IMarkdownFormattingResult => insertText(formattingArgs, this.pullRequestCharacter);
}

const maxFileSizeMB = 25;
export const maxFileSize = maxFileSizeMB * 1024 * 1024;

type AttachmentCommandProps = Omit<WorkItemFormMarkdownToolbarProps, keyof IMarkdownToolbarProps>;
export class AttachmentCommand implements IMarkdownToolbarCommand {
    public id = "markdown-attachment";
    public iconProps: IIconProps = { iconName: "Attach" };
    public text = Resources.MarkdownEditorInsertFileTitle;
    public rank = 113;

    private props: AttachmentCommandProps;

    constructor(props: AttachmentCommandProps) {
        this.props = props;
    }

    public formatText = (
        formattingArgs: IMarkdownFormattingArgs,
        errorHandler: (error: Error | null) => void
    ): Promise<IMarkdownFormattingResult | void> => {
        const { pageContext, uploadImage } = this.props;
        const attachmentsValid = new ObservableValue(false);
        const isUploading = new ObservableValue(false);
        let file: FileInputData | undefined;

        return new Promise<IMarkdownFormattingResult | void>((resolve, reject) => {
            const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            layoutManager.renderCallout(onDismiss => (
                <Observer attachmentsValid={attachmentsValid} isUploading={isUploading}>
                    {props => (
                        <MessageDialog
                            lightDismiss={false}
                            title={Resources.MarkdownEditorUploadFileDialogTitle}
                            okButtonProps={{
                                disabled: !props.attachmentsValid || props.isUploading,
                                text: Resources.MarkdownEditorUploadFileDialogSubmit
                            }}
                            onClose={async result => {
                                if (result) {
                                    try {
                                        if (file) {
                                            isUploading.value = true;
                                            const uploadedImageUrl = await uploadImage(file.result.file!);
                                            resolve(insertText(formattingArgs, `![${file.result.name}](${uploadedImageUrl}) `));
                                        } else {
                                            resolve(insertText(formattingArgs));
                                        }
                                        errorHandler(null);
                                    } catch (e) {
                                        const formattedError = new Error(format(Resources.MarkdownEditorUploadFileError, e.message));
                                        errorHandler(formattedError);
                                        reject(e);
                                    } finally {
                                        isUploading.value = false;
                                    }
                                } else {
                                    resolve(insertText(formattingArgs));
                                }

                                onDismiss();
                            }}
                            message={
                                props.isUploading ? (
                                    <IndeterminateProgressBar loading />
                                ) : (
                                    <FileInput
                                        allowedFileExtensions={validFileTypeList}
                                        maximumNumberOfFiles={1}
                                        maximumSingleFileSize={maxFileSize}
                                        resultContentType={FileInputContentType.RawFile}
                                        updateHandler={updateEvent => {
                                            attachmentsValid.value = updateEvent.files.length > 0;
                                            if (updateEvent.files.length > 0) {
                                                file = updateEvent.files[0];
                                            } else {
                                                file = undefined;
                                            }
                                        }}
                                    />
                                )
                            }
                        />
                    )}
                </Observer>
            ));
        });
    };
}

class HighlightCommand implements IMarkdownToolbarCommand {
    private highlightStart = "<mark>";
    private highlightEnd = "</mark>";
    public id = "highlight";
    public iconProps: IIconProps = { iconName: "FabricTextHighlight" };
    public text = Resources.MarkdownEditorHighlight;
    public rank = 114;

    public formatText = (formattingArgs: IMarkdownFormattingArgs): IMarkdownFormattingResult =>
        wrapWithSign(formattingArgs, this.highlightStart, this.highlightEnd);
}
